import { useMemo } from 'react'

import { matchFilterNumber, matchFilterString, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { BrandProviderModel, BrandProviderRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['brandProviders']

type BrandProviderFilters = {
  brand_id?: number,
  brand_title?: string,
  provider_id?: number,
  provider_title?: string,
}

type UseBrandProvidersOptions = {
  filters?: BrandProviderFilters,
  performHttpRequests?: boolean,
  requestOptions?: BrandProviderRequestOptions,
  sortKey?: 'brand' | 'provider',
}

function useBrandProviders(options: UseBrandProvidersOptions) {
  const { filters = {}, requestOptions, sortKey } = options
  const {
    brand_id: filterBrandId,
    brand_title: filterBrandTitle,
    provider_id: filterProviderId,
    provider_title: filterProviderTitle,
  } = filters

  const {
    updatedEntities: { brandProviders: brandProvidersUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { brands, brandProviders, providers } = useSelector(reduxState => reduxState.entities)

  const filteredBrandProviders = useMemo(() => {
    const filtered = Object.values(brandProviders).filter((brandProvider: BrandProviderModel) => {
      const { brand_id, provider_id } = brandProvider
      const brandTitle = brands[brand_id]?.title
      const providerTitle = providers[provider_id]?.title

      const matchBrandId = brand_id?.toString() === filterBrandTitle
      const matchBrandTitle = matchFilterString(brandTitle, filterBrandTitle)
      const matchBrandString = matchBrandId || matchBrandTitle

      const matchProviderId = provider_id?.toString() === filterProviderTitle
      const matchProviderTitle = matchFilterString(providerTitle, filterProviderTitle)
      const matchProviderString = matchProviderId || matchProviderTitle

      const matchBrand = matchFilterNumber(brand_id, filterBrandId)
      const matchProvider = matchFilterNumber(provider_id, filterProviderId)

      return matchBrand && matchBrandString && matchProvider && matchProviderString
    })

    return sortArrayBy(
      filtered,
      'asc',
      ({ brand_id, provider_id }) => (sortKey === 'brand' ? brands[brand_id]?.title : providers[provider_id]?.title),
    )
  }, [brandProvidersUpdatedAt, JSON.stringify(filters)])

  const filteredBrandProvidersCount = filteredBrandProviders.length
  const hasFilteredBrandProviders = !!filteredBrandProvidersCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandProvidersCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandProviders } = useReduxAction(
    'brandProviders',
    'loadBrandProviders',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandProviders,
    filteredBrandProvidersCount,
    hasFilteredBrandProviders,
    loading: loadingBrandProviders,
  }
}

export default useBrandProviders
